import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Seo, { breadcrumbLd, faqLd, SITE_URL } from "@/components/Seo";
import { MarketingNav, MarketingFooter, MarketingCTA } from "@/components/MarketingChrome";
import { getGuide, GUIDES } from "@/data/seoContent";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); }
  catch { return ""; }
};

export default function GuidePage() {
  const { slug } = useParams();
  const g = getGuide(slug);
  if (!g) return <Navigate to="/resources" replace />;

  const jsonLd = [
    breadcrumbLd([
      { name: "Home", path: "/" },
      { name: "Guides", path: "/resources" },
      { name: g.title, path: `/resources/${g.slug}` },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: g.title,
      description: g.description,
      datePublished: g.date,
      dateModified: g.date,
      author: { "@type": "Organization", name: "Ask Redline" },
      publisher: { "@type": "Organization", name: "Ask Redline", url: SITE_URL },
      mainEntityOfPage: `${SITE_URL}/resources/${g.slug}`,
    },
    ...(g.faqs ? [faqLd(g.faqs)] : []),
  ];

  const others = GUIDES.filter((o) => o.slug !== g.slug).slice(0, 3);

  return (
    <div className="font-body bg-sand-50 text-ink min-h-screen">
      <Seo title={`${g.title} | Ask Redline`} description={g.description} canonical={`/resources/${g.slug}`} type="article" jsonLd={jsonLd} scrollTop />
      <MarketingNav />

      <article className="max-w-3xl mx-auto px-6 pt-14 pb-8">
        <Link to="/resources" className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink-500 hover:text-redline-600 transition-colors mb-6" data-testid="guide-back">
          <ArrowLeft className="h-4 w-4" /> All guides
        </Link>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="text-[11px] uppercase tracking-[0.16em] font-bold text-redline-600">{g.category}</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.12] mt-3" data-testid="guide-title">{g.title}</h1>
          <div className="mt-4 text-sm text-ink-500">{fmtDate(g.date)} · {g.readMins} min read</div>
          <p className="mt-6 text-lg text-ink-700 leading-relaxed">{g.description}</p>
        </motion.div>
      </article>

      <div className="max-w-3xl mx-auto px-6 pb-12">
        <div className="prose prose-stone prose-lg max-w-none
          prose-headings:font-heading prose-headings:tracking-tight prose-headings:text-ink
          prose-h2:text-2xl prose-h2:font-extrabold prose-h2:mt-10 prose-h2:mb-3
          prose-p:text-ink-700 prose-p:leading-relaxed
          prose-a:text-redline-600 prose-a:font-semibold prose-a:no-underline hover:prose-a:underline
          prose-strong:text-ink prose-li:text-ink-700 prose-li:marker:text-redline-500"
          data-testid="guide-body">
          <ReactMarkdown
            components={{
              a: ({ href, children }) =>
                href && href.startsWith("/")
                  ? <Link to={href}>{children}</Link>
                  : <a href={href} target="_blank" rel="noreferrer">{children}</a>,
            }}
          >
            {g.body}
          </ReactMarkdown>
        </div>
      </div>

      {g.faqs && (
        <section className="max-w-3xl mx-auto px-6 pb-12">
          <h2 className="font-heading text-2xl font-extrabold tracking-tight mb-5">FAQ</h2>
          <div className="space-y-3">
            {g.faqs.map((f, i) => (
              <div key={i} className="rounded-2xl border border-sand-200 bg-white p-5" data-testid={`guide-faq-${i}`}>
                <div className="font-heading font-bold text-ink">{f.q}</div>
                <p className="text-ink-700 text-sm mt-2 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="text-xs uppercase tracking-[0.18em] font-bold text-ink-400 mb-4">Keep reading</div>
        <div className="grid sm:grid-cols-3 gap-4">
          {others.map((o) => (
            <Link key={o.slug} to={`/resources/${o.slug}`} className="group rounded-2xl border border-sand-200 bg-white p-5 hover:border-redline-300 transition-colors" data-testid={`guide-related-${o.slug}`}>
              <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-ink-400">{o.category}</span>
              <div className="font-heading font-bold text-ink mt-2 leading-snug group-hover:text-redline-600 transition-colors">{o.title}</div>
              <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-redline-600">Read <ArrowRight className="h-4 w-4" /></span>
            </Link>
          ))}
        </div>
      </section>

      <MarketingCTA />
      <MarketingFooter />
    </div>
  );
}
